import {
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  Focusable,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  CardTitle,
  compactDuration,
  StatusChip,
  type StatusChipStatus,
  waadooIcons,
  formatTimestamp,
  projectEnvironmentsTranslationRef,
} from '@internal/plugin-waadoo-core';
import { odooIcons } from '../odooIcons/odooIcons';
import {
  imageTag,
  initReportPhase,
  MODULE_STATE_LABEL_KEYS,
  moduleStateKey,
  moduleStateStatus,
  type InitReport,
  type InitReportPhase,
} from './initReport';
import { odooTranslationRef } from './translation';

/**
 * Up to this many names, a module list is shown whole; past it (a first
 * initialisation installs a hundred modules and more), only the project's
 * custom modules are, the others folded behind « + N autres ».
 */
export const MODULES_SHOWN = 12;

export interface InitReportCardProps {
  /** The environment's report, read by the page (`useInitReport.ts`). */
  report: InitReport;
  /** The instant the page rendered at: what the phase and ages are read at. */
  now: number;
  /** The card's element id: what the overview's banner scrolls to. */
  id?: string;
  /**
   * The Logs tab on the job's pod (`branchLogsPath`), given by the page
   * only while its resource tree lists that pod; no link without it.
   */
  logsHref?: string;
}

/** The chip of each phase, and its words. */
const PHASE_CHIPS = {
  succeeded: { status: 'healthy', label: 'initReport.status.succeeded' },
  failed: { status: 'failed', label: 'initReport.status.failed' },
  running: { status: 'progressing', label: 'initReport.status.running' },
  interrupted: { status: 'failed', label: 'initReport.status.interrupted' },
} as const satisfies Record<
  InitReportPhase,
  { status: StatusChipStatus; label: string }
>;

/**
 * One list of module names (« Installés », « Mis à jour », and for a run in
 * progress « À installer », « À mettre à jour »), as small neutral
 * pills: « aucun » when empty; whole up to {@link MODULES_SHOWN} names;
 * past it, the custom modules of the list, then the others behind a native
 * `<details>` — closed, its pills are still in the document, which is
 * harmless here: plain list items, nothing focusable (the reason `PlatformStrip`
 * mounts its node cards only once open does not apply).
 */
function ModuleNames(props: {
  label: string;
  names: string[];
  custom: ReadonlySet<string>;
}) {
  const { label, names, custom } = props;
  const { t } = useTranslationRef(odooTranslationRef);
  if (names.length === 0) {
    return (
      <div className="waadoo-init-report__section">
        <Text variant="body-small" weight="bold">
          {label}
        </Text>
        <Text variant="body-small" color="secondary">
          {t('initReport.none')}
        </Text>
      </div>
    );
  }
  const folded = names.length > MODULES_SHOWN;
  const shown = folded ? names.filter(name => custom.has(name)) : names;
  const rest = folded ? names.filter(name => !custom.has(name)) : [];
  const pills = (list: string[], listLabel: string) => (
    <ul className="waadoo-init-report__pills" aria-label={listLabel}>
      {/* Keyed by position too: nothing upstream removes a duplicate. */}
      {list.map((name, index) => (
        <li key={`${index}-${name}`} className="waadoo-pill">
          {name}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="waadoo-init-report__section">
      <Text variant="body-small" weight="bold">
        {label}
      </Text>
      {shown.length > 0 && pills(shown, label)}
      {rest.length > 0 && (
        <details className="waadoo-init-report__details">
          <summary>
            <Text variant="body-small" color="secondary">
              {rest.length === 1
                ? t('initReport.othersOne')
                : t('initReport.others', { n: String(rest.length) })}
            </Text>
          </summary>
          {pills(rest, label)}
        </details>
      )}
    </div>
  );
}

/**
 * « Modules », on the overview tab of the branch page, after the resources:
 * the report of the last run of the Odoo chart's init job (`initReport.ts`)
 * — whether it succeeded, when, how long it took, whether it was the first
 * initialisation of the database or an update, the image it ran; then the
 * modules it installed and updated, the state of each of the project's
 * custom modules (open from the start when one of them is not installed),
 * and for a failed run the end of its log, folded.
 *
 * A RUN IN PROGRESS (chart 0.9.7): « En cours » since its start, and the
 * plan — the modules it is to install and to update — in place of what it
 * did. Past `INIT_REPORT_STALE_MS`, « Interrompue »: the job's pod died
 * before its final write, and nothing more will come. While the job's pod
 * is listed (`logsHref`), a link to its log in the Logs tab: live while it
 * runs, its last lines for a failed or interrupted run whose pod is still
 * there (a finished job pod is not listed: `extractPods` keeps running
 * pods only, and the Job is deleted 10 min after its end).
 *
 * Presentational: the page reads the report (`useInitReport.ts`, which
 * polls while the run is in progress) and renders this only WITH one — no
 * report is the normal state of every environment of a chart before 0.9.6,
 * and a card saying so would be noise nobody can act on.
 *
 * The log tail is the job's own text, rendered as a React text child: never
 * interpreted as HTML.
 */
export function InitReportCard(props: InitReportCardProps) {
  const { report, now, id, logsHref } = props;
  const { t } = useTranslationRef(odooTranslationRef);
  // La locale de la page et son « Voir le log du job », commun avec le
  // bandeau d'échec du déploiement.
  const { t: tPage } = useTranslationRef(projectEnvironmentsTranslationRef);

  const locale = tPage('format.locale');
  const phase = initReportPhase(report, now);
  const chip = PHASE_CHIPS[phase];
  const custom = new Set(report.customModules.map(module => module.name));
  const customPending = report.customModules.some(
    module => module.state !== 'installed',
  );
  const tag = report.image ? imageTag(report.image) : '';
  const ImageIcon = waadooIcons.image;
  const LogsIcon = waadooIcons.logs;
  const started = Date.parse(report.startedAt);

  return (
    // Focusable by script only (tabIndex -1): the banner's « Voir les
    // modules » moves the focus here with the scroll, so that a keyboard
    // or screen reader user lands on the card too.
    <Card id={id} tabIndex={-1} className="waadoo-init-report-card">
      <CardHeader>
        <CardTitle
          icon={odooIcons.odooModules}
          title={t('initReport.title')}
          description={t('initReport.subtitle')}
        />
      </CardHeader>
      <CardBody>
        <div className="waadoo-init-report">
          <div className="waadoo-init-report__head">
            <Text variant="body-small">
              <StatusChip status={chip.status} label={t(chip.label)} />
            </Text>
            {report.status === 'running' ? (
              <Text
                variant="body-small"
                color="secondary"
                title={t('initReport.startedTitle', {
                  started: formatTimestamp(report.startedAt, locale),
                })}
              >
                {t(
                  phase === 'running'
                    ? 'initReport.runningSince'
                    : 'initReport.startedAgo',
                  { age: compactDuration(started, now) },
                )}
              </Text>
            ) : (
              <>
                <Text
                  variant="body-small"
                  color="secondary"
                  title={t('initReport.timesTitle', {
                    started: formatTimestamp(report.startedAt, locale),
                    finished: formatTimestamp(report.finishedAt, locale),
                  })}
                >
                  {t('initReport.finishedAgo', {
                    age: compactDuration(Date.parse(report.finishedAt), now),
                  })}
                </Text>
                <Text variant="body-small" color="secondary">
                  {t('initReport.duration', {
                    duration: compactDuration(0, report.durationSeconds * 1000),
                  })}
                </Text>
              </>
            )}
            <Text variant="body-small">
              {t(
                report.mode === 'init'
                  ? 'initReport.mode.init'
                  : 'initReport.mode.update',
              )}
            </Text>
            {/* The tag alone on the pill, the whole reference in its
                tooltip and accessible name — the pattern of the pod
                badges (PodPlacementBits.tsx). */}
            {tag && (
              <TooltipTrigger>
                <Focusable>
                  <span
                    role="img"
                    aria-label={t('initReport.imageName', {
                      image: report.image,
                    })}
                    className="waadoo-pill waadoo-init-report__image"
                  >
                    <ImageIcon aria-hidden focusable="false" />
                    {tag}
                  </span>
                </Focusable>
                <Tooltip>{report.image}</Tooltip>
              </TooltipTrigger>
            )}
          </div>

          {phase === 'interrupted' && (
            <Text variant="body-small" color="danger">
              {t('initReport.interrupted')}
            </Text>
          )}

          {/* Not for a succeeded run: its pod, done, is no longer listed —
              and the page gives no href then anyway. */}
          {logsHref && phase !== 'succeeded' && (
            <div>
              <ButtonLink
                href={logsHref}
                variant="secondary"
                size="small"
                iconStart={<LogsIcon aria-hidden focusable="false" />}
              >
                {phase === 'running'
                  ? t('initReport.liveLog')
                  : tPage('initReport.jobLog')}
              </ButtonLink>
            </div>
          )}

          {report.status === 'running' ? (
            <>
              <ModuleNames
                label={t('initReport.plannedInstall')}
                names={report.plannedInstall}
                custom={custom}
              />
              <ModuleNames
                label={t('initReport.plannedUpdate')}
                names={report.plannedUpdate}
                custom={custom}
              />
            </>
          ) : (
            <>
              <ModuleNames
                label={t('initReport.installed')}
                names={report.installed}
                custom={custom}
              />
              <ModuleNames
                label={t('initReport.updated')}
                names={report.updated}
                custom={custom}
              />
            </>
          )}

          {report.customModules.length > 0 && (
            <details
              className="waadoo-init-report__details"
              // Uncontrolled: React sets it on mount and leaves the reader's
              // toggling alone. A poll's new report object does not change
              // it either: React only writes a prop whose value changed.
              open={customPending}
            >
              <summary
                title={
                  report.customHash
                    ? t('initReport.customHashTitle', {
                        hash: report.customHash,
                      })
                    : undefined
                }
              >
                <Text variant="body-small" weight="bold">
                  {t('initReport.custom', {
                    n: String(report.customModules.length),
                  })}
                </Text>
              </summary>
              <ul className="waadoo-init-report__custom">
                {report.customModules.map((module, index) => {
                  const key = moduleStateKey(module.state);
                  return (
                    <li
                      key={`${index}-${module.name}`}
                      className="waadoo-init-report__row"
                    >
                      <Text variant="body-small" weight="bold">
                        {module.name}
                      </Text>
                      {module.version && (
                        <Text variant="body-small" color="secondary">
                          {module.version}
                        </Text>
                      )}
                      <Text variant="body-small">
                        <StatusChip
                          status={moduleStateStatus(key)}
                          label={
                            key ? t(MODULE_STATE_LABEL_KEYS[key]) : module.state
                          }
                        />
                      </Text>
                    </li>
                  );
                })}
              </ul>
            </details>
          )}

          {report.status === 'failed' && report.logTail && (
            <details className="waadoo-init-report__details">
              <summary>
                <Text variant="body-small" weight="bold">
                  {t('initReport.logTail')}
                </Text>
              </summary>
              <pre
                className="waadoo-init-report__log"
                // Scrollable, so reachable and named for the keyboard — the
                // exception LogsCard.tsx documents for its log box.
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                role="region"
                aria-label={t('initReport.logTail')}
              >
                {report.logTail}
              </pre>
            </details>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
